import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, HttpMethod } from '@api/common';
import { queryKeys, showApiError } from '@api/queryClient';
import toastService from '@services/toastService';
import { TankBalance, TankSurfaceLevel } from './tanks';

// ============================================================================
// Types
// ============================================================================

export interface Measurement {
  id: string;
  tankId: string;
  tankName: string;
  productId: string;
  productName: string;
  bookkeepingEntryId?: string;
  affectsBalance: boolean;
  hasApprovedEntry: boolean;
  timestamp: string;
  value: number;
  notes: string;
  created: string;
  createdBy: string;
  modified?: string;
  modifiedBy?: string;
}

export interface MeasurementCmd {
  id?: string;
  tankId: string;
  productId: string;
  timestamp: string;
  value: number;
  notes: string;
  affectsBalance: boolean;
}

// ============================================================================
// API Functions
// ============================================================================

const API_ENDPOINT = 'measurements';

const fetchMeasurements = async (tankId: string): Promise<Measurement[]> => {
  return apiRequest<void, Measurement[]>(HttpMethod.GET, `${API_ENDPOINT}?tankId=${tankId}`);
};

const fetchMeasurement = async (id: string): Promise<Measurement> => {
  return apiRequest<void, Measurement>(HttpMethod.GET, `${API_ENDPOINT}/${id}`);
};

const createMeasurement = async (measurement: MeasurementCmd): Promise<Measurement> => {
  return apiRequest<MeasurementCmd, Measurement>(HttpMethod.POST, API_ENDPOINT, measurement);
};

const updateMeasurement = async (measurement: MeasurementCmd): Promise<Measurement> => {
  return apiRequest<MeasurementCmd, Measurement>(HttpMethod.PUT, `${API_ENDPOINT}/${measurement.id}`, measurement);
};

const deleteMeasurement = async (id: string): Promise<void> => {
  return apiRequest<void, void>(HttpMethod.DELETE, `${API_ENDPOINT}/${id}`);
};

const fetchTankBalance = async (
  tankId: string,
  productId: string,
  datetime: string,
  excludeEntryId?: string
): Promise<TankBalance> => {
  const url = `tank/${tankId}/balance?productId=${productId}&datetime=${datetime}&excludeEntryId=${excludeEntryId || ''}`;
  return apiRequest<void, TankBalance>(HttpMethod.GET, url);
};

const fetchTankSurfaceLevel = async (tankId: string, datetime: string): Promise<TankSurfaceLevel> => {
  return apiRequest<void, TankSurfaceLevel>(HttpMethod.GET, `tank/${tankId}/surfacelevel?datetime=${datetime}`);
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all measurements for a specific tank
 */
export const useMeasurementsQuery = (tankId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.measurements.list({ tankId }),
    queryFn: () => fetchMeasurements(tankId!),
    enabled: !!tankId,
  });
};

/**
 * Fetch a single measurement by ID
 */
export const useMeasurementQuery = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.measurements.detail(id!),
    queryFn: () => fetchMeasurement(id!),
    enabled: !!id,
  });
};

/**
 * Fetch tank balance for a specific product at a given time
 * Used in measurement editor to show current bookkeeping balance
 */
export const useTankBalanceQuery = (
  tankId: string | undefined,
  productId: string | undefined,
  datetime: string | undefined,
  excludeEntryId?: string
) => {
  return useQuery({
    queryKey: queryKeys.measurements.list({ tankId, productId, datetime, excludeEntryId, type: 'tankBalance' }),
    queryFn: () => fetchTankBalance(tankId!, productId!, datetime!, excludeEntryId),
    enabled: !!tankId && !!productId && !!datetime,
  });
};

/**
 * Fetch tank surface level (radar reading) at a given time
 * Used in measurement editor to show current radar balance
 */
export const useTankSurfaceLevelQuery = (tankId: string | undefined, datetime: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.measurements.list({ tankId, datetime, type: 'tankSurfaceLevel' }),
    queryFn: () => fetchTankSurfaceLevel(tankId!, datetime!),
    enabled: !!tankId && !!datetime,
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new measurement
 */
export const useCreateMeasurementMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMeasurement,
    onSuccess: (data) => {
      // Invalidate measurements list for the tank
      queryClient.invalidateQueries({ queryKey: queryKeys.measurements.list({ tankId: data.tankId }) });
      // Also invalidate tank balances as measurements may affect them
      queryClient.invalidateQueries({ queryKey: queryKeys.tanks.balances() });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Update an existing measurement
 */
export const useUpdateMeasurementMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateMeasurement,
    onSuccess: (data) => {
      // Invalidate measurements list for the tank
      queryClient.invalidateQueries({ queryKey: queryKeys.measurements.list({ tankId: data.tankId }) });
      // Also invalidate tank balances as measurements may affect them
      queryClient.invalidateQueries({ queryKey: queryKeys.tanks.balances() });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Delete a measurement
 */
export const useDeleteMeasurementMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteMeasurement,
    onSuccess: () => {
      // Invalidate all measurements lists (we don't have the tankId in the success callback)
      queryClient.invalidateQueries({ queryKey: queryKeys.measurements.all });
      // Also invalidate tank balances
      queryClient.invalidateQueries({ queryKey: queryKeys.tanks.balances() });
      toastService.success({ title: 'measurements.measurement_removed' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};
